import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Signal,
    signal,
    untracked,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom } from 'rxjs';

import { LicenseListBloc } from '@connect-services/client/blocs/license-list.bloc';
import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { AppLogoComponent } from '@connect-services/client/components/app-logo/app-logo.component';
import { ManagementListHeaderComponent } from '@connect-services/client/components/management/management-list-header/management-list-header.component';
import { UserCreateModalComponent } from '@connect-services/client/components/management/users-roles/create-modal/user-create-modal/user-create-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { PaginationSdkParams } from '@connect-services/client/services/fetch-all.service';
import { GlobalSearchService } from '@connect-services/client/services/global-search.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { RoleService } from '@connect-services/client/services/role.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { TableService } from '@connect-services/client/services/table.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import {
    License,
    Person,
    PersonControllerGet200Response,
    Role,
    RoleControllerGet200Response,
    RolesService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

type PersonWithLicenses = Person & { licenses: License.ApplicationEnum[] };

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AppLogoComponent,
        CustomTableComponent,
        FormsModule,
        ManagementListHeaderComponent,
        NoDataToDisplayComponent,
        PaginatorComponent,
        RessourcesModule,
    ],
    selector: 'app-users-list',
    styleUrl: './users-list.component.scss',
    templateUrl: './users-list.component.html',
})
export class UsersListComponent {
    public readonly assignedLicenses: WritableSignal<Set<License>> = signal(new Set<License>());
    public readonly licensesAssignedCount = signal(0);
    public readonly maxLicensesAvailable = signal(0);
    public readonly paginatedPeople: WritableSignal<PersonControllerGet200Response | null> = signal(null);
    public paginationParameters: PaginationParameters = {
        ...defaultPaginationParameters,
        order: OrderEnum.ASC,
        orderBy: 'familyName',
        take: 4,
    };
    public readonly peopleListBloc = inject(PeopleListBloc);
    public readonly rolesById: WritableSignal<Record<string, Role> | null> = signal(null);
    public readonly usersWithLicensesToDisplay: WritableSignal<PersonWithLicenses[] | null> = signal<
        PersonWithLicenses[] | null
    >(null);

    protected readonly canCreateUser = computed(() => this.organizationAccessPermissionsService.canManageUser());
    protected readonly License = License;
    protected tableColumns: CustomColumn[] = [];
    protected userDetailPanelOutletRegex: RegExp = /\(detailPanel:user\/([a-zA-Z0-9-]+)(?:\/edition)?\)/;
    protected readonly userInPanel: WritableSignal<string | null> = signal(null);

    private readonly detailPanelService = inject(DetailPanelService);
    private readonly globalSearchService = inject(GlobalSearchService);
    private readonly licenseListBloc = inject(LicenseListBloc);
    private readonly matDialog = inject(MatDialog);
    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly paginator: Signal<PaginatorComponent | undefined> = viewChild<PaginatorComponent>('paginator');
    private readonly roleService = inject(RoleService);
    private readonly rolesService = inject(RolesService);
    private readonly router = inject(Router);
    private readonly subscriptionService = inject(SubscriptionService);
    private readonly tableService = inject(TableService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.tableColumns = [
            {
                key: 'familyName',
                label: 'name',
            },
            {
                key: 'givenName',
                label: 'givenName',
            },
            {
                key: 'email',
                label: 'email',
            },
            {
                key: 'role',
                label: this.translocoService.translate('role', { COUNT: 1 }),
            },
            {
                key: 'license',
                label: 'license',
                sortable: false,
            },
        ];

        this.globalSearchService.setSearchProperties();
        this.togglePanel();

        this.router.events.pipe(filter((event: Event) => event instanceof NavigationEnd)).subscribe(() => {
            this.togglePanel();
        });

        effect(async () => {
            if (!this.peopleListBloc.value()) {
                return;
            }

            this.paginatedPeople.set(this.peopleListBloc.value());

            await untracked(() => this.initAssignedLicenses());
            this.usersWithLicensesToDisplay.set(this.setPeopleWithLicenses(this.paginatedPeople()!.items));
        });

        effect(async () => {
            const roles = await this.fetchRoles();

            const roleId = this.roleService.getRoleById(roles);
            this.rolesById.set(roleId);
        });
    }

    public hasRemainingLicenses(): boolean {
        if (!this.maxLicensesAvailable()) {
            return false;
        }

        return this.licensesAssignedCount() < this.maxLicensesAvailable();
    }

    public async onPaginationChange(page: number): Promise<void> {
        const pageInformation: PaginationSdkParams = {
            page: page,
            take: this.paginatedPeople()!.take,
        };

        this.paginationParameters = this.tableService.onPaginationChange(pageInformation, this.paginationParameters);
        await this.loadPeopleListBloc(this.globalSearchService.searchValue()!);
    }

    public async onSortChange(sortState?: SortState): Promise<void> {
        if (!sortState) {
            return;
        }

        sortState = {
            ...sortState,
            order: sortState.order ?? OrderEnum.ASC,
        };

        this.paginationParameters = this.tableService.onSortChange(sortState, this.paginationParameters);
        this.paginator()?.resetPaginator();
        await this.loadPeopleListBloc(this.globalSearchService.searchValue()!);
    }

    public async onUserToggle(person: Person): Promise<void> {
        if (!this.organizationAccessPermissionsService.canAccessUser()) {
            return;
        }
        const searchParams = this.globalSearchService.searchValue();
        const panelParam = person.id === this.userInPanel() ? null : person.id;

        if (!panelParam) {
            await this.router.navigate([...RouteCompletePathEnum.USERS_ROLES]);
            this.globalSearchService.searchValue.set(searchParams);

            return;
        }

        await this.router.navigate([
            ...RouteCompletePathEnum.USERS_ROLES,
            { outlets: { detailPanel: ['user', panelParam] } },
        ]);
        this.globalSearchService.searchValue.set(searchParams);
    }

    public async openCreateUserModal(): Promise<void> {
        if (!this.canCreateUser()) {
            return;
        }

        if (this.detailPanelService.isOpen()) {
            await this.router.navigate(this.detailPanelService.navigateTo());
        }
        this.matDialog.open(UserCreateModalComponent, {
            width: '45rem',
        });
    }

    public async searchUsers(searchValue: string): Promise<void> {
        this.globalSearchService.searchValue.set(searchValue);
        this.paginator()?.resetPaginator();
        await this.loadPeopleListBloc(this.globalSearchService.searchValue()!);
    }

    private async fetchAllAssignedLicenses(subscriptionId: string): Promise<void> {
        const licenses: Set<License> = new Set<License>();
        const paginationParameters = { ...defaultPaginationParameters, take: paginationMaxTake };

        do {
            await this.licenseListBloc.load(
                {
                    ...paginationParameters,
                    assignedOnly: true,
                    subscription: subscriptionId,
                },
                { refresh: true },
            );

            const paginatedLicenses = this.licenseListBloc.value()!;
            paginatedLicenses.items.forEach((license: License) => {
                licenses.add(license);
            });

            paginationParameters.page! += 1;
        } while (this.licenseListBloc.value()!.hasNextPage);

        this.assignedLicenses.set(licenses);
        this.licensesAssignedCount.set(this.licenseListBloc.value()!.totalItems);
    }

    private async fetchRoles(): Promise<Role[]> {
        const roles: Role[] = [];
        const rolePaginationParameters = { ...defaultPaginationParameters, take: paginationMaxTake };

        let page = defaultPaginationParameters.page;

        let hasNextPage: boolean;

        do {
            const paginatedRoles: RoleControllerGet200Response = await firstValueFrom(
                this.rolesService.roleControllerGet({ ...rolePaginationParameters, page: page }),
            );

            if (paginatedRoles.items?.length) {
                roles.push(...paginatedRoles.items);
            }

            hasNextPage = paginatedRoles.hasNextPage;

            if (hasNextPage) {
                page += 1;
            }
        } while (hasNextPage);

        return roles;
    }

    private async initAssignedLicenses(): Promise<void> {
        const subscription = await this.subscriptionService.getSubscription();
        this.maxLicensesAvailable.set(
            subscription.maxConnectServicesLicense +
                subscription.maxConnectFieldLicense +
                subscription.maxConnectControlLicense,
        );
        await this.fetchAllAssignedLicenses(subscription.id);
    }

    private async loadPeopleListBloc(searchValue?: string): Promise<void> {
        await this.peopleListBloc.load(
            {
                ...this.paginationParameters,
                ...(searchValue && { properties: this.globalSearchService.columnsToSearchValueIn }),
                value: searchValue,
            },
            { refresh: true },
        );
    }

    private setPeopleWithLicenses(people: Person[]): PersonWithLicenses[] {
        return people.map((person: Person) => {
            const licenses = [...this.assignedLicenses()].filter((license: License) => {
                return license.person === person.id;
            });

            return Object.assign(person, {
                licenses: licenses
                    .map((license: License) => license.application)
                    .sort()
                    .reverse(),
            });
        });
    }

    private togglePanel(): void {
        if (!this.router.url.match(this.userDetailPanelOutletRegex)) {
            this.userInPanel.set(null);

            return;
        }
        this.userInPanel.set(this.router.url.match(this.userDetailPanelOutletRegex)![1]);
    }
}
